// Modules
import {useRouter} from "next/router";
import Image from 'next/image';

// Components
import SearchBar from "components/shared/SearchBar/SearchBar";
import wkLogo from "../../../../public/wk-sdf-logo.svg";

// Redux
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { isMobileSearchSelector, setIsMobileSearch } from "redux/searchPage/searchBar/searchBarSlice";

// Types
import {UserInfo} from "interfaces/shared/userInfo";

// Constants
import {PROFILO_UTENTE_TAB_URL, CORSI_TAB_URL, ATTESTATI_TAB_URL} from "constants/shared/pages";

// Utils
import {buildLoginRequest, buildLogoutRequest} from "utils/shared/sessionHandler";

interface HeaderProps {
	loading: boolean;
	userInfoClientSide: UserInfo;
	isHeaderBurgerOpen: boolean;
	setIsHeaderBurgerOpen: (isHeaderBurgerOpen: boolean) => void;
	isProfileOpen: boolean;
	setIsProfileOpen: (isProfileOpen: boolean) => void;
}

const Header = ({loading, userInfoClientSide, isHeaderBurgerOpen, setIsHeaderBurgerOpen, isProfileOpen, setIsProfileOpen }: HeaderProps) => {
	const { isLogged, userName, hrefchart, numelchart } = userInfoClientSide;
	const dispatch = useAppDispatch();
	const isMobileSearch = useAppSelector(isMobileSearchSelector);

	const router = useRouter();
	const { asPath } = router;

	const handleProfileOpen = () => {
		setIsHeaderBurgerOpen(true);
		setIsProfileOpen(false);
	};

	return (
		<>
			<header className="wk-header" role="banner">
				<div className="wk-header-container">
					<button className="wk-sticky-header-burger wk-icon-menu"
							onClick={() => setIsHeaderBurgerOpen(!isHeaderBurgerOpen)} aria-label="header-burger"/>
					<a className="wk-logo-section" href="/">
						<img className="wk-logo-small"
							 src="https://cdn.wolterskluwer.io/wk-logos/1.0.x/wk-brand-small.svg"
							 alt="Wolters Kluwer Product Name - Page anchor links to"/>
						<div className="wk-logo-large">
							<Image src={wkLogo} width={400} height={28} alt="Wolters Kluwer Product Name - Page anchor links to" priority={true}></Image>
						</div>
					</a>
					<div className="wk-header-tools">
						<button type="button" className="wk-search-submit wk-search-submit-mobile"
								onClick={() => dispatch(setIsMobileSearch(!isMobileSearch))} aria-label="search-submit">
							<span className={"wk-icon-search"}/>
						</button>
						<div className="wk-header-search-form" style={{width: isMobileSearch ? '100%' : '60%', marginRight: '16px'}}>
							<SearchBar mobileSearch={isMobileSearch}/>
						</div>
						{loading ? (
							<span className="wk-spin wk-icon-spinner"
								  style={{width: '20%', marginRight: '48px'}}></span>
						) : (
							<>
								<div className="wk-header-nav">
									<ul className="wk-nav">
										<div>
											<div className={`wk-dropdown-profile ${isProfileOpen ? "wk-open" : ""}`}>
												<button className="wk-dropdown-profile-toggle" aria-haspopup="true"
														aria-expanded="false"
														onClick={() => setIsProfileOpen(!isProfileOpen)}>
													{isLogged ? (
														<>
															<span className="wk-icon-user-check"/>
															<span className="wk-dropdown-button-label">{userName}</span>
														</>
													) : (
														<>
															<span className="wk-icon-user-plus"/>
															<span className="wk-dropdown-button-label">Accedi</span>
														</>
													)}
													<span className="wk-icon-chevron-down"/>
													<span className="wk-icon-chevron-up wk-hidden"/>
												</button>
												<div className="wk-dropdown-profile-menu" aria-hidden="true"
													 aria-label="submenu" aria-labelledby="button-dropdown-right">
													<div className="wk-dropdown-profile-header">
														<button className="wk-back-button" onClick={handleProfileOpen}>
															<span className="wk-icon-arrow-left"/>
														</button>
														<span className="wk-icon-user-check"/>
													</div>
													<ul className="wk-dropdown-profile-body">
														{isLogged ? (
															<>
																<li>
																	<a href={"/user/iscritto/AreaPersonale.aspx?tab=profilo-utente"}>
																		Il mio profilo
																		<span className="wk-icon-arrow-right"/>
																	</a>
																</li>
																<li>
																	<a href={"/user/iscritto/AreaPersonale.aspx?tab=corsi"}>
																		I miei corsi
																		<span className="wk-icon-arrow-right"/>
																	</a>
																</li>
																<li>
																	<a href={"/user/iscritto/AreaPersonale.aspx?tab=attestati"}>
																		I miei attestati
																		<span className="wk-icon-arrow-right"/>
																	</a>
																</li>
															</>
														) : (
															<>
																<li>
																	<a href={buildLoginRequest(PROFILO_UTENTE_TAB_URL)}>
																		Il mio profilo
																		<span className="wk-icon-arrow-right"/>
																	</a>
																</li>
																<li>
																	<a href={buildLoginRequest(CORSI_TAB_URL)}>
																		I miei corsi
																		<span className="wk-icon-arrow-right"/>
																	</a>
																</li>
																<li>
																	<a href={buildLoginRequest(ATTESTATI_TAB_URL)}>
																		I miei attestati
																		<span className="wk-icon-arrow-right"/>
																	</a>
																</li>
															</>
														)}

													</ul>
													<div className="wk-dropdown-menu-footer">
														{isLogged ? (
															<a className="wk-logout-button" href={buildLogoutRequest(asPath)}>
																<span className="wk-icon-arrow-square-left"></span>
																<span>Esci</span>
															</a>
														) : (
															<span>
													Non hai ancora un account?
													<a href={"/user/iscritto/AreaPersonale.aspx?tab=form_iscrizione"}>Registrati</a>
												</span>
														)}
													</div>
												</div>
											</div>
										</div>
									</ul>
									<div>
										<div className="wk-mini-cart">
											<a href={hrefchart} aria-label="cart">
											<span className="wk-icon-cart wk-notification-badge"
												  data-notification-count={numelchart > 0 ? numelchart : null}/>
											</a>
										</div>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			</header>
		</>
	);
};

export default Header;
